import {
	LightbulbLit,
	LogoText2,
	NavigationalLeftArrow
} from '@/resources/assets/images';
import Button from '@/components/Button/newButtons';
import { useState } from 'react';
import { ErrorMessage } from '@/components/atoms/ErrorMessage/ErrorMessage';
import { RootState } from '@/rootReducer';
import { useSelector } from 'react-redux';
import { sendAnalytics } from '@/services/analyticsService';
import { useOnboardingContext } from '../context/OnboardingContext';

export default function OnboardingOffering() {
	const { onboardingData, setOnboardingData, nextPage, previousPage } =
		useOnboardingContext();
	const [offeringError, setOfferingError] = useState<string | null>(null);
	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);
	const handleNextClick = () => {
		const length = onboardingData.offering?.length ?? 0;

		if (length < 1) {
			setOfferingError(
				'Please fill in what you are offering before continuing.'
			);
			return;
		}

		if (length > 1000) {
			setOfferingError('Your offer must be under 1000 characters.');
			return;
		}

		setOfferingError(null);
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'Onboarding Flow - Submitted offer',
			properties: {
				page: 'Step 2: Offer Page',
				offering: onboardingData.offering
			}
		});
		nextPage();
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleNextClick();
		}
	};

	const variant =
		user?.features?.onboardingTestGroup === 'test'
			? 'OnboardingFlowA'
			: 'OnboardingFlowB';

	const getCopy = () => {
		if (variant === 'OnboardingFlowA') {
			return {
				title: 'What are you offering them?',
				body: (
					<>
						<span className="font-barlow text-base font-normal leading-8 text-dm-white sm:leading-8">
							This is the core of your pitch. We&apos;ll use it to
							auto-write messaging that&apos;s worked for other
							businesses like yours — all tuned to{' '}
							<span className="font-bold text-dm-gold">
								your offer
							</span>
							.
						</span>
					</>
				)
			};
		}

		// Variant B (default)
		return {
			title: 'What are you selling?',
			body: (
				<>
					<span className="font-barlow text-base font-normal leading-8 text-dm-white sm:leading-8">
						Tell us{' '}
						<span className="font-bold text-dm-gold">
							what you&apos;re offering
						</span>{' '}
						and Resquared will turn it into a sequence of emails and
						follow-ups designed to convert.
						<br />
						Be clear — this is what we&apos;ll pitch on your behalf.
					</span>
				</>
			)
		};
	};

	const copy = getCopy();

	return (
		<div className="h-full w-full overflow-hidden">
			{/* Top Header */}
			<div className="h-[3.75rem] w-full overflow-hidden rounded-tl-lg rounded-tr-lg border-b border-t border-dm-gray px-4 sm:h-[6.375rem] sm:px-20 lg:rounded-tr-none lg:border-l">
				<div className="flex h-full w-full items-center">
					<div className="mx-auto flex w-full max-w-[79rem] items-center justify-between">
						<div className="flex items-center gap-4">
							<LightbulbLit className="h-[2rem] w-[1.375rem] shrink-0" />
							<h1 className="font-barlow text-lg font-semibold leading-[1.375rem] text-dm-white">
								Step 2 of 4: Your Offer
							</h1>
						</div>

						<LogoText2 className="h-6 w-[6.5625rem]" />
					</div>
				</div>
			</div>

			{/* Main Content */}
			<div className="h-full border-dm-gray px-4 py-6 sm:py-10 sm:pl-12 sm:pr-16 lg:border-l">
				<div
					data-flow-a={variant === 'OnboardingFlowA'}
					data-flow-b={variant === 'OnboardingFlowB'}
					className="mx-auto flex w-full max-w-[79rem] flex-col gap-2 overflow-hidden rounded-2xl border border-dm-gray bg-dm-black3/50 px-4 pt-8 data-[flow-a=true]:min-h-[23.188rem] data-[flow-b=true]:min-h-[25.188rem] sm:px-20"
				>
					<Button
						type="button"
						variant="text"
						className="flex rounded-lg p-0 text-dm-white sm:hidden"
						onClick={previousPage}
						leftIcon={<NavigationalLeftArrow />}
					>
						Back
					</Button>
					<div className="flex flex-1 flex-col gap-10 overflow-auto pb-8 sm:overflow-visible sm:pb-0">
						<div className="flex flex-col gap-4">
							{/* Step Title */}
							<span className="font-barlow text-xl font-semibold leading-8 tracking-normal text-dm-white sm:text-2xl sm:leading-10">
								{copy.title}
							</span>

							{/* Body Text */}
							<div className="max-w-[41rem] font-barlow text-sm font-normal leading-7 text-dm-white sm:text-base sm:leading-8">
								{copy.body}
							</div>
							{/* Input */}
							<div className="flex flex-col gap-1">
								<input
									autoFocus
									type="text"
									placeholder="E.g. Website design and Google reviews help"
									className="h-[2.188rem] w-full max-w-[25.313rem] rounded-[4px] border border-gray-300 bg-white px-3 py-2 font-barlow text-sm font-normal leading-[1.188rem] tracking-normal text-black focus:border-blue-500 focus:outline-none sm:text-base"
									value={onboardingData.offering}
									onKeyDown={handleKeyDown}
									onChange={e =>
										setOnboardingData({
											offering: e.target.value
										})
									}
								/>
								<div className="-mt-1 h-[1rem]">
									{offeringError && (
										<ErrorMessage message={offeringError} />
									)}
								</div>
							</div>
						</div>

						{/* Button */}
						<div className="flex flex-col justify-start gap-7 sm:flex-row sm:gap-14">
							<Button
								type="button"
								variant="secondary"
								className="hidden rounded-lg px-6 text-white sm:flex"
								onClick={previousPage}
							>
								Go Back
							</Button>
							<Button
								type="button"
								smart
								variant="primary"
								className="rounded-lg px-6 text-white"
								onClick={handleNextClick}
							>
								Next: Why They Should Care
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
